import * as FileSaver from 'file-saver';
import { action, computed, makeObservable, observable } from 'mobx';

import ApiService, { FileExtension, InvoiceFileType, Source } from '../services/ApiService';
import { registerError } from '../util/ErrorUtil';

import EmployeeUsagesStore from './EmployeeUsagesStore';
import InvoiceDetailsStore from './InvoiceDetailsStore';
import { DownloadableFile } from './models/DownloadableFile';
import { IInvoiceDetailsRequest } from './models/IInvoiceDetailsRequest';
import { Loadable } from './models/Loadable';
import { IRouteStore } from './RouteStore';
import { ISsoStore } from './SsoStore';
import { InvoiceFileTypesRequest } from './models/InvoiceFileTypesRequest';
import { InvoiceFileRequest } from './models/InvoiceFileRequest';

export type FileDownloadMethod = 'URL' | 'API_REQUEST';

export default class InvoiceFileStore {
  @observable
  public fileTypes: Loadable<InvoiceFileType[]> = new Loadable<InvoiceFileType[]>();
  @observable
  private invoiceFiles: Map<string, Loadable<DownloadableFile>> = new Map();

  constructor(
    private ssoStore: ISsoStore,
    private apiService: ApiService,
    private employeeUsagesStore: EmployeeUsagesStore,
    private invoiceDetailsStore: InvoiceDetailsStore,
    private routeStore: IRouteStore
  ) {
    makeObservable(this);
  }

  @action
  public async loadFileTypes(): Promise<any> {
    if (this.invoiceDetailsStore.invoiceNotFound) {
      return Promise.resolve('Invoice not found');
    }
    const { invoiceNumber, source } = this.invoiceDetailsStore.invoiceDetailsRequest;

    const request: InvoiceFileTypesRequest = {
      invoiceNumber: invoiceNumber,
      source: source,
      customerId: this.ssoStore.customer.customerId
    };
    const fileTypePromise = this.apiService.getInvoiceFileTypes(request);
    this.fileTypes = Loadable.toLoadable(fileTypePromise);
    await fileTypePromise;
  }

  public getInvoiceFileTypes(): InvoiceFileType[] {
    return this.fileTypes.payload;
  }

  public getFile(invoiceFileType: InvoiceFileType, invoiceNumber: string): Loadable<DownloadableFile> {
    return this.getOrCreate(InvoiceFileStore.invoiceFileIdentification(invoiceFileType, invoiceNumber));
  }

  @computed
  public get sortedInvoiceFileTypes(): InvoiceFileType[] {
    const { invoiceServiceDetailsAccess } = this.invoiceDetailsStore.invoiceDetailsRequest;

    if (!this.getInvoiceFileTypes()) {
      return null;
    }

    return this.sortAndFilterInvoiceFileTypes(this.getInvoiceFileTypes(), invoiceServiceDetailsAccess, this.invoiceDetailsStore.isCurrentInvoiceRestricted);
  }

  public downloadFile(invoiceDetailsRequest: IInvoiceDetailsRequest, invoiceFileType: InvoiceFileType, downloadMethod: FileDownloadMethod): void {
    if (this.employeeUsagesStore.usagesDownloadRestrictedForDealer(invoiceFileType)) {
      this.employeeUsagesStore.addRestrictedEmployeeFiles(invoiceFileType);
      return;
    }
    if (downloadMethod === 'API_REQUEST') {
      this.downloadInvoiceFile(invoiceDetailsRequest.invoiceNumber, invoiceDetailsRequest.source, invoiceFileType);
    }
    if (downloadMethod === 'URL') {
      this.routeStore.goToExternalUrl(this.invoiceFileUrl(invoiceDetailsRequest.invoiceNumber, invoiceDetailsRequest.source, invoiceFileType));
    }
  }

  public approveAndDownloadUsages(invoiceNumber: string, source: Source): void {
    if (this.employeeUsagesStore.showEmployeeInvoiceUsagesHeaderNotice) {
      this.downloadInvoiceFile(invoiceNumber, source, this.employeeUsagesStore.restrictedEmployeeFile);
    }
    this.employeeUsagesStore.changeHasCheckedUsagesTrueAndDontShowNotice();
  }

  public getFileExtensionFromInvoiceFileType(fileName: InvoiceFileType): FileExtension {
    const fileType: string[] = fileName.split('_');

    return fileType[1] as FileExtension;
  }

  public hasError(invoiceNumber: string): boolean {
    let hasError = false;
    for (const fileType of this.getInvoiceFileTypes()) {
      hasError = hasError || this.getFile(fileType, invoiceNumber).isFinishedWithError;
    }
    return hasError;
  }

  public saveFile(loadableFile: Loadable<DownloadableFile>): void {
    const file = loadableFile.payload;
    FileSaver.saveAs(file.rawData, file.fileName);
  }

  public sortInvoiceFileTypes(invoiceFileType: InvoiceFileType[]): InvoiceFileType[] {
    const sortedInvoiceFileTypes = [];

    sortedInvoiceFileTypes.push(invoiceFileType.find((type) => type === 'INVOICE_PDF'));
    sortedInvoiceFileTypes.push(invoiceFileType.find((type) => type === 'USAGES_CSV'));
    sortedInvoiceFileTypes.push(invoiceFileType.find((type) => type === 'INVOICE_CSV'));
    sortedInvoiceFileTypes.push(invoiceFileType.find((type) => type === 'INVOICE_XML'));
    sortedInvoiceFileTypes.push(invoiceFileType.find((type) => type === 'EXTRAS_PDF'));

    return sortedInvoiceFileTypes.filter((type) => !!type);
  }

  public filterInvoiceFileTypes(
    invoiceFileTypes: InvoiceFileType[],
    invoiceServiceDetailsAccess: boolean,
    isCurrentInvoiceRestricted: boolean
  ): InvoiceFileType[] {
    return invoiceFileTypes.filter((fileType) => {
      if (isCurrentInvoiceRestricted) {
        return false;
      }
      if ((fileType === 'INVOICE_XML' || fileType === 'INVOICE_CSV') && this.ssoStore.isPrivateCustomer) {
        return false;
      } else if (fileType === 'USAGES_CSV' && !invoiceServiceDetailsAccess) {
        return false;
      }

      return true;
    });
  }

  private getOrCreate(invoiceFileName: string): Loadable<DownloadableFile> {
    if (this.invoiceFiles.get(invoiceFileName) === undefined) {
      this.createFile(invoiceFileName);
    }
    return this.invoiceFiles.get(invoiceFileName);
  }

  @action.bound
  private createFile(invoiceFileName: string) {
    const file = new Loadable<DownloadableFile>();
    this.invoiceFiles.set(invoiceFileName, file);
  }

  private static invoiceFileIdentification(invoiceFileType: InvoiceFileType, invoiceNumber: string) {
    return invoiceFileType.toString() + invoiceNumber;
  }

  private sortAndFilterInvoiceFileTypes(
    invoiceFileTypes: InvoiceFileType[],
    invoiceServiceDetailsAccess: boolean,
    isCurrentInvoiceRestricted: boolean
  ): InvoiceFileType[] {
    invoiceFileTypes = this.filterInvoiceFileTypes(invoiceFileTypes, invoiceServiceDetailsAccess, isCurrentInvoiceRestricted);
    invoiceFileTypes = this.sortInvoiceFileTypes(invoiceFileTypes);
    return invoiceFileTypes;
  }

  private downloadInvoiceFile(invoiceNumber: string, source: Source, invoiceFileType: InvoiceFileType): void {
    const loadableFile = this.getOrCreate(InvoiceFileStore.invoiceFileIdentification(invoiceFileType, invoiceNumber));
    if (loadableFile.isFinished) {
      this.saveFile(loadableFile);
      return;
    }
    if (loadableFile.isLoading) {
      return;
    }
    this.setInvoiceFileDownloading(loadableFile, invoiceNumber, source, invoiceFileType);
  }

  @action.bound
  private setInvoiceFileDownloading(loadableFile: Loadable<DownloadableFile>, invoiceNumber: string, source: Source, invoiceFileType: InvoiceFileType): void {
    const request: InvoiceFileRequest = {
      invoiceNumber: invoiceNumber,
      source: source,
      customerId: this.ssoStore.customer.customerId,
      fileType: invoiceFileType
    };
    loadableFile.setLoading();
    const invoiceFilePromise: Promise<DownloadableFile> = this.apiService.downloadInvoiceFile(request);
    invoiceFilePromise
      .then((file: DownloadableFile) => {
        loadableFile.setSuccess(file);
        this.saveFile(loadableFile);
      })
      .catch((err) => {
        if (err.code === 'ECONNABORTED' || (err.response != null && err.response.status === 404)) {
          // try to download it again
          this.setInvoiceFileDownloading(loadableFile, invoiceNumber, source, invoiceFileType);
          return;
        }

        loadableFile.setError();
        registerError(err);
      });
  }

  useNativeDownload(invoiceFileType: InvoiceFileType): boolean {
    return invoiceFileType === 'USAGES_CSV' && this.ssoStore.isBusinessCustomer;
  }

  invoiceFileUrl(invoiceNumber: string, source: Source, invoiceFileType: InvoiceFileType): string {
    const request: InvoiceFileRequest = {
      invoiceNumber: invoiceNumber,
      source: source,
      customerId: this.ssoStore.customer.customerId,
      fileType: invoiceFileType
    };
    return this.apiService.fullInvoiceFileUrl(request);
  }
}
